import { Component, type ErrorInfo, type ReactNode } from "react";
import { useLocation } from "react-router";
import { cn } from "../lib/cn";
import { IconDanger, IconRefresh } from "./icons";
import { Button } from "./ui/Button";

/*
 * Catches render errors so one broken screen doesn't take down the shell. The
 * fallback offers a retry (re-mounts the subtree); a failed lazy chunk (usually a
 * new deploy) offers a reload instead. Route boundaries reset on navigation.
 */

interface Props {
  children: ReactNode;
  /** Changing this clears the error (e.g. the pathname). */
  resetKey?: unknown;
  /** Short name for the area, used in the message ("Feed couldn't load"). */
  label?: string;
  className?: string;
  /** Smaller fallback for panes and panels. */
  compact?: boolean;
}

interface State {
  error: Error | null;
  attempt: number;
}

const isChunkError = (e: Error) => /dynamically imported module|Importing a module script failed|Failed to fetch dynamically|ChunkLoadError|Loading chunk/i.test(e.message);

export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null, attempt: 0 };

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.warn("[atrium] view crashed", error, info.componentStack);
  }

  componentDidUpdate(prev: Props) {
    if (this.state.error && prev.resetKey !== this.props.resetKey) this.setState({ error: null });
  }

  retry = () => this.setState((s) => ({ error: null, attempt: s.attempt + 1 }));

  render() {
    const { error, attempt } = this.state;
    if (!error) return <ErrorBoundaryKey key={attempt}>{this.props.children}</ErrorBoundaryKey>;
    const chunk = isChunkError(error);
    return (
      <div role="alert" className={cn("flex h-full flex-col items-center justify-center gap-3 px-6 text-center", this.props.compact ? "py-8" : "py-16", this.props.className)}>
        <span className="flex size-10 items-center justify-center rounded-xl bg-surface-2 text-fg-2">
          <IconDanger className="icon size-5" aria-hidden />
        </span>
        <div>
          <h2 className="text-base font-semibold text-fg">{this.props.label ? `${this.props.label} couldn't load` : "Something went wrong"}</h2>
          <p className="mt-1 max-w-sm text-sm text-fg-3">
            {chunk ? "A newer version of Atrium is available." : "This part of the app hit a problem. Your other tabs and drafts are fine."}
          </p>
        </div>
        <Button variant="secondary" size="sm" icon={IconRefresh} onClick={chunk ? () => window.location.reload() : this.retry}>
          {chunk ? "Reload" : "Try again"}
        </Button>
      </div>
    );
  }
}

/** Re-keys children on retry so they mount fresh. */
function ErrorBoundaryKey({ children }: { children: ReactNode }) {
  return <>{children}</>;
}

/** Boundary for a routed page: resets when the path changes. */
export function RouteBoundary({ children, label }: { children: ReactNode; label?: string }) {
  const { pathname } = useLocation();
  return (
    <ErrorBoundary resetKey={pathname} label={label}>
      {children}
    </ErrorBoundary>
  );
}
